import { createContext, useContext } from 'react'

/**
 * Portal-level override for the endpoints the shared stats frameworks call.
 *
 * The clinic stats visualizations (ClinicReportsWithVisualizations / ModernizedClinicReportsPage)
 * and the practitioner "My Reports" insights (PractitionerReportInsightsPanel →
 * usePractitionerReportInsights) default to the mentor-portal endpoints below. The support portal
 * wraps the SAME components in a StatsApiContext.Provider pointing at the read-only admin
 * endpoints (/api/secure/cc/support/clinic-stats/* and /api/secure/cc/support/practitioner-stats/*),
 * so any enhancement to the shared components automatically lands on both portals.
 */
export const DEFAULT_STATS_API = Object.freeze({
  clinicReportsPath: '/api/secure/clinic-admin/reports/get-reports',
  patientSegmentationPath: '/api/secure/clinic-admin/reports/patient-segmentation',
  practitionerInsightsPath: '/api/secure/practitioner/report-insights',
  // Extra query params merged into every insights call — the admin endpoint needs the explicit
  // { mentorId } since the practitioner can't be resolved from the admin's auth context.
  practitionerInsightsExtraParams: null,
  // The practitioner's own endpoint requires a clinic location; the admin endpoint treats it as
  // optional (omitted → aggregate ACROSS ALL CLINICS). Admin surfaces flip this to false so the
  // shared hook fires without a clinicLocationId.
  practitionerInsightsRequiresClinicLocation: true,
})

export const StatsApiContext = createContext(DEFAULT_STATS_API)

export function useStatsApi() {
  return useContext(StatsApiContext) || DEFAULT_STATS_API
}
